import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import { ItemType } from "assets/types/itemTypes";
// import ItemTable from "Components/ItemsTable";
import { autocompleteDropdownStyles } from "Constants/Style";
import { QuantityInput } from "Components/Other/quantityInput";
import { calculateItemValues } from "helpers/functions/calculateItemValues";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";

type AttributesTypes = {
  id: string;
  name: string;
};

type Category = {
  category_id: string;
  category_name: string;
  is_mandatory: boolean;
  attributes: AttributesTypes[];
};

type SelectedItemRow = ItemType & {
  original_selling_price?: number; // original selling price from database (for validation)
  brand_name?: string;
  sr_no?: number; // serial number to preserve order of items
};

// Actions column background color
const ACTIONS_COLUMN_BG_COLOR = "#e3f2fd";

const Standalone = ({ itemList, setItemList }: any) => {
  document.title = "Standalone Quotation";

  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<ItemType[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );

  // console.log("itemList here:", itemList);

  const [selectedItem, setSelectedItem] = useState<ItemType | null>(null);
  const [selectedItemsList, setSelectedItemsList] = useState<SelectedItemRow[]>(
    []
  );
  const [quantity, setQuantity] = useState<number>(1);

  const [categoryLoading, setCategoryLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Populate selectedItemsList in edit mode
  // NOTE: This component is ONLY used for standalone quotations, so sr_no logic applies here
  useEffect(() => {
    setItemsLoading(true);
    setTimeout(() => {
      if (!itemList) {
        setItemsLoading(false);
        return;
      }
      const allItems: SelectedItemRow[] = [];
      Object.keys(itemList).forEach((key) => {
        itemList[key].forEach((item: any) => {
          allItems.push(
            calculateItemValues({
              ...item,
              quantity: item.quantity || 1,
              tax_rate: Number(item.gst),
              gst: Number(item.gst),
              discount: item.discount ?? 0,
              discount_type: item.discount_type ?? "%",
              original_selling_price:
                item.original_selling_price ?? item.selling_price, // Store original price
              sr_no: item.sr_no ?? undefined, // Preserve sr_no from backend (standalone only)
            }) as SelectedItemRow
          );
        });
      });

      // Sort items by sr_no to maintain order (standalone items only - items without sr_no go to end)
      allItems.sort((a, b) => {
        const srNoA = a.sr_no ?? Number.MAX_SAFE_INTEGER;
        const srNoB = b.sr_no ?? Number.MAX_SAFE_INTEGER;
        return srNoA - srNoB;
      });

      setSelectedItemsList((prev) =>
        JSON.stringify(prev) === JSON.stringify(allItems) ? prev : allItems
      );
      setItemsLoading(false);
    }, 500);
  }, []);
  //*// itemlist removed

  // fetching All categories
  useEffect(() => {
    const fetchCategoriesList = async () => {
      setCategoryLoading(true);
      try {
        let response = await Axios.get("/api/category/category-dropdown/");
        const updatedCat: Category[] = response.data.data.map((cat: any) => ({
          ...cat,
        }));

        setCategories(updatedCat);
      } catch (error) {
        console.error("Failed to fetch categories", error);
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategoriesList();
  }, []);

  // fetching Items based on category
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedCategory) {
        setItems([]); // clear when deselected
        return;
      }

      // 🔹 Clear current items immediately
      setItems([]);
      setSelectedItem(null);
      setItemsLoading(true);

      const categoryId = selectedCategory.category_id;
      const categoryName = selectedCategory.category_name ?? "";

      try {
        const params = new URLSearchParams({ category_id: categoryId });
        const response: any = await Axios.get(
          `/api/item/by-category-without-pagination/?${params.toString()}`
        );

        const itemData = response?.data?.data[categoryName] || [];

        setItems(
          itemData.map((it: ItemType) => ({
            ...it,
            quantity: 1,
          }))
        );
      } catch (error) {
        toast.error(`Failed to fetch ${categoryName}`);
      } finally {
        setItemsLoading(false);
      }
    };

    fetchData();
  }, [selectedCategory]);

  const addItem = (categoryId: string, item: ItemType, quantity: number) => {
    setSelectedItemsList((prev: any) => {
      const exists = prev.find(
        (p: any) => p.item_id === item.item_id && p.category === categoryId
      );

      if (exists) {
        return prev.map((p: any) =>
          p.item_id === item.item_id &&
          p.category === categoryId &&
          p.quantity < 10
            ? calculateItemValues({
                ...p,
                quantity: Math.max(0, (p.quantity || 1) + quantity),
              })
            : p
        );
      }

      // Calculate next sr_no: find max sr_no and add 1, or start from 1 if no items
      // NOTE: sr_no is ONLY used for standalone quotations to preserve item order
      const maxSrNo = prev.length > 0 
        ? Math.max(...prev.map((p: any) => p.sr_no ?? 0), 0)
        : 0;
      const nextSrNo = maxSrNo + 1;

      // Pass the new item through calculateItemValues
      const newItem = calculateItemValues({
        ...item,
        category: categoryId,
        tax_rate: Number(item.gst),
        gst: Number(item.gst),
        quantity: quantity,
        discount: 0,
        discount_type: "%",
        original_selling_price: item.selling_price, // Store original price when item is first added
        sr_no: nextSrNo, // Assign serial number (standalone only)
      }) as SelectedItemRow;

      return [...prev, newItem];
    });
  };

  const updateItemField = (
    item_id: string,
    categoryId: string,
    field: string,
    value: any
  ) => {
    setSelectedItemsList((prev) =>
      prev.map((item) =>
        item.item_id === item_id && item.category === categoryId
          ? calculateItemValues({ ...item, [field]: value })
          : item
      )
    );
  };

  const removeItem = (itemId: string, categoryId: string) => {
    setSelectedItemsList((prev) =>
      prev.filter((p) => !(p.item_id === itemId && p.category === categoryId))
    );
  };

  const setQty = (itemId: string, categoryId: string, qty: number) => {
    setSelectedItemsList((prev) =>
      prev.map((p) =>
        p.item_id === itemId && p.category === categoryId
          ? calculateItemValues({
              ...p,
              quantity: Math.max(1, Math.min(1000, Math.floor(qty || 1))), // min 1, max 1000
            })
          : p
      )
    );
  };

  const handleAdd = () => {
    if (!selectedItem || !selectedCategory) return;

    addItem(
      selectedCategory.category_id,
      {
        ...selectedItem,
        category_name: selectedCategory.category_name,
      },
      quantity
    );

    setSelectedCategory(null);
    setSelectedItem(null);
    setQuantity(1);

    toast.success("Item Added!");
  };

  useEffect(() => {
    const normalizeKey = (name: string) =>
      name
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");

    // Sort items by sr_no before grouping by category (standalone only)
    const sortedItems = [...selectedItemsList].sort((a, b) => {
      const srNoA = a.sr_no ?? Number.MAX_SAFE_INTEGER;
      const srNoB = b.sr_no ?? Number.MAX_SAFE_INTEGER;
      return srNoA - srNoB;
    });

    const item_details: Record<string, any[]> = {};

    sortedItems.forEach((it) => {
      const payloadRow: any = {
        item_id: it.item_id,
        category: it.category,
        category_name: it.category_name,
        item_name: it.item_name,
        item_sku: it.item_sku,
        selling_price: Number(it.selling_price ?? 0).toFixed(2),
        gst: Number(it.gst ?? 0).toFixed(2),
        tax_rate: it.tax_rate,
        discount: it.discount,
        discount_type: it.discount_type,
        taxable_value: it.taxable_value,
        tax_amount: it.tax_amount,
        item_amount: it.item_amount,
        quantity: it.quantity,
        remarks: "",
        tags: null,
        tag_name: null,
        image: null,
        attributes: it.attributes,
        brand_name: it.brand_name,
      };

      // Only include sr_no if it exists (standalone quotations only)
      if (it.sr_no !== undefined && it.sr_no !== null) {
        payloadRow.sr_no = it.sr_no;
      }

      const key = normalizeKey(it.category_name);

      if (!item_details[key]) {
        item_details[key] = []; // create only when needed
      }

      item_details[key].push(payloadRow);
    });

    setItemList(item_details);
  }, [selectedItemsList]);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(val);

  return (
    <div className="bgback py-3 px-4 my-3 rounded">
      {categoryLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <Row className="g-2 align-items-end">
            {/* Category Select */}
            <Col xs={12} sm={4} md={3}>
              <Form.Label className="mb-1">
                <span className="text-danger">*</span> Category
              </Form.Label>
              <Autocomplete
                disablePortal
                size="small"
                fullWidth
                options={categories} // use fetched categories
                getOptionLabel={(option) => option.category_name} // show category name
                value={selectedCategory}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (option) => option.category_name
                  )
                }
                onChange={(_, newValue) => setSelectedCategory(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Category"
                    variant="outlined"
                    size="small"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      backgroundColor: "#fff",
                    }}
                  />
                )}
              />
            </Col>

            {/* Item Select */}
            <Col xs={12} sm={5} md={4}>
              <Form.Label className="mb-1">
                <span className="text-danger">*</span> Item
              </Form.Label>
              <Autocomplete
                disablePortal
                size="small"
                fullWidth
                options={items}
                getOptionLabel={(option) => option.item_name}
                value={selectedItem}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (option) => option.item_name
                  )
                }
                onChange={(_, newValue) => setSelectedItem(newValue)}
                loading={itemsLoading} // 👈 add this
                loadingText="Loading items..." // 👈 custom loading text
                noOptionsText={
                  selectedCategory
                    ? "No items found"
                    : "Select a category first"
                } // 👈 helpful UX
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Item"
                    variant="outlined"
                    size="small"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      backgroundColor: "#fff",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {itemsLoading ? (
                            <Spinner size="sm" animation="border" />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Col>

            {/* Quantity Input */}
            <Col xs={6} sm={2} md={1}>
              <Form.Label className="mb-1">Qty</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="text-center"
                style={{ fontFamily: "Poppins, sans-serif" }}
              />
            </Col>

            {/* Add Button */}
            <Col xs={12} sm={2} md={2}>
              <Button
                disabled={!selectedCategory || !selectedItem || quantity <= 0}
                variant="primary"
                onClick={handleAdd}
                className="w-sm-auto py-2 px-3"
              >
                {/* {itemsLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      // className="me-2 btn btn-primary"
                    />{" "}
                    Loading..
                  </>
                ) : (
                  "+ Add"
                )} */}
                {!selectedCategory || !selectedItem || quantity <= 0
                  ? "Choose"
                  : "+ Add"}
              </Button>
            </Col>
          </Row>

          {/* Table Header Only */}
          {/* {(selectedCategory || selectedItem) && ( */}

          <div className="my-4 rounded border-black">
            <div className="table-responsive table-card rounded-4 mx-0 m-lg-1 border">
              {selectedItemsList.length !== 0 && (
                <Table className="table table-bordered table-centered align-middle table-custom-effect table-nowrap m-0 p-0border-blue">
                  <thead
                    className="table-abgb"
                    style={{ backgroundColor: "#0070b6ff", color: "#fff" }}
                  >
                    <tr>
                      <th style={{ color: "#fff" }} className="px-3">
                        Category
                      </th>
                      <th style={{ color: "#fff" }} className="px-3">
                        Item Name
                      </th>
                      <th style={{ color: "#fff" }} className="px-3">
                        Brand
                      </th>
                      <th style={{ color: "#fff" }} className="px-3">
                        Item SKU
                      </th>
                      <th style={{ color: "#fff" }} className="px-3">
                        Selling Price
                      </th>
                      <th style={{ color: "#fff" }} className="px-3">
                        Quantity
                      </th>
                      <th style={{ color: "#fff" }} className="px-3">
                        Discount
                      </th>
                      {/* <th style={{ color: "#fff" }} className="px-3">
                        GST %
                      </th> */}
                      <th style={{ color: "#fff" }} className="px-3">
                        Total
                      </th>
                      <th
                        style={{
                          color: "#fff",
                          width: 100,
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#0070b6ff",
                          zIndex: 10,
                          borderLeft: "2px solid rgba(0, 0, 0, 0.1)"
                        }}
                        className="px-3"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...selectedItemsList]
                      .sort((a, b) => {
                        // Sort by sr_no to maintain order (standalone items only)
                        const srNoA = a.sr_no ?? Number.MAX_SAFE_INTEGER;
                        const srNoB = b.sr_no ?? Number.MAX_SAFE_INTEGER;
                        return srNoA - srNoB;
                      })
                      .map((item, idx) => {
                      const brandName =
                        item.brand_name || "-";
                      return (
                        <tr key={item.item_id + item.sr_no + idx}>
                          <td
                            style={{ backgroundColor: "#fff" }}
                            className="px-3"
                          >
                            {item?.category_name}
                          </td>
                          <td
                            style={{ backgroundColor: "#fff", maxWidth: 300 }}
                            className="text-truncate"
                            title={item.item_name}
                          >
                            {item.item_name}
                          </td>
                          <td
                            style={{ backgroundColor: "#fff", maxWidth: 150 }}
                            className="text-truncate"
                          >
                            {brandName}
                          </td>
                          <td
                            style={{ backgroundColor: "#fff", maxWidth: 250 }}
                            className="text-truncate"
                          >
                            {item.item_sku}
                          </td>
                          <td
                            style={{ backgroundColor: "#fff", maxWidth: 120 }}
                          >
                            <Form.Control
                              type="number"
                              value={item.selling_price}
                              size="sm"
                              className="form-control form-control-sm text-center"
                              style={{
                                width: "100px",
                                height: "34px",
                                padding: "0.25rem",
                                fontFamily: "Poppins, sans-serif",
                              }}
                              min={
                                item.original_selling_price ??
                                item.selling_price
                              }
                              step="0.01"
                              onChange={(e) => {
                                let val = Number(e.target.value);

                                // Handle empty input
                                if (isNaN(val)) {
                                  return; // Don't update if invalid input
                                }

                                // Update the selling price (which will trigger recalculation)
                                // Validation will happen on blur
                                updateItemField(
                                  item.item_id,
                                  item.category,
                                  "selling_price",
                                  val
                                );
                              }}
                              onBlur={(e) => {
                                let val = Number(e.target.value);

                                // Handle empty input
                                if (isNaN(val) || !e.target.value) {
                                  // Reset to original selling_price if empty
                                  const originalPrice =
                                    item.original_selling_price ??
                                    item.selling_price;
                                  e.target.value = String(originalPrice);
                                  updateItemField(
                                    item.item_id,
                                    item.category,
                                    "selling_price",
                                    originalPrice
                                  );
                                  return;
                                }

                                const originalPrice =
                                  item.original_selling_price ??
                                  item.selling_price;

                                // Ensure value is not less than original price
                                if (val < originalPrice) {
                                  toast.error(
                                    `Selling price cannot be less than original price (${formatCurrency(
                                      originalPrice
                                    )})`
                                  );
                                  // Reset input to original selling_price
                                  e.target.value = String(originalPrice);
                                  updateItemField(
                                    item.item_id,
                                    item.category,
                                    "selling_price",
                                    originalPrice
                                  );
                                  return;
                                }

                                // Ensure value is positive
                                if (val < 0) {
                                  toast.error(
                                    "Selling price cannot be negative"
                                  );
                                  // Reset input to original selling_price
                                  e.target.value = String(originalPrice);
                                  updateItemField(
                                    item.item_id,
                                    item.category,
                                    "selling_price",
                                    originalPrice
                                  );
                                  return;
                                }
                              }}
                            />
                          </td>

                          <td
                            style={{ backgroundColor: "#fff", maxWidth: 120 }}
                          >
                            <QuantityInput
                              value={item.quantity}
                              maxQty={10000}
                              onChange={(val) =>
                                setQty(item.item_id, item.category, val)
                              }
                            />
                          </td>

                          {/* Discount column */}
                          <td
                            style={{ backgroundColor: "#fff" }}
                            className="d-flex gap-2 align-items-center"
                          >
                            <Form.Control
                              type="number"
                              value={item.discount}
                              size="sm"
                              className="form-control form-control-sm text-center"
                              style={{
                                width: "60px",
                                height: "34px",
                                padding: "0.25rem",
                                fontFamily: "Poppins, sans-serif",
                              }}
                              onChange={(e) => {
                                let val = Number(e.target.value);

                                if (item.discount_type === "%") {
                                  // Clamp percentage discount between 0 and 100
                                  val = Math.max(0, Math.min(val, 100));
                                } else {
                                  // Flat discount cannot exceed total line price
                                  const maxFlatDiscount =
                                    item.selling_price * item.quantity;
                                  val = Math.max(
                                    0,
                                    Math.min(val, maxFlatDiscount)
                                  );
                                }

                                updateItemField(
                                  item.item_id,
                                  item.category,
                                  "discount",
                                  val
                                );
                              }}
                            />
                            <Form.Select
                              value={item.discount_type}
                              size="sm"
                              onChange={(e) =>
                                updateItemField(
                                  item.item_id,
                                  item.category,
                                  "discount_type",
                                  e.target.value
                                )
                              }
                              className="text-center"
                              style={{
                                width: "45px",
                                height: "34px",
                                padding: 0, // remove internal padding completely
                                lineHeight: "1", // align text vertically
                                border: "1px solid #ced4da", // optional, keep consistent with row
                                borderRadius: "0", // remove rounded corners if you want flush
                                appearance: "none",
                                WebkitAppearance: "none",
                                MozAppearance: "none",
                                // background: "transparent", // optional, avoid extra background
                                background: `url("data:image/svg+xml;utf8,<svg fill='black' height='10' viewBox='0 0 24 24' width='10' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>") no-repeat right 4px center`,
                                backgroundColor: "white",
                                backgroundSize: "12px",
                                fontFamily: "Poppins, sans-serif",
                              }}
                            >
                              <option value="%">%</option>
                              <option value="flat">₹</option>
                            </Form.Select>
                          </td>

                          {/* Tax rate (GST) editable */}
                          {/* <td
                            style={{ backgroundColor: "#fff", maxWidth: 60 }}
                          >
                            <input
                              type="number"
                              value={item.gst}
                              readOnly
                              className="form-control form-control-sm text-center bg-light"
                            />
                          </td> */}

                          <td style={{ backgroundColor: "#fff" }}>
                            {formatCurrency(item.item_amount || 0)}
                          </td>
                          <td
                            style={{
                              position: "sticky",
                              right: 0,
                              backgroundColor: ACTIONS_COLUMN_BG_COLOR,
                              zIndex: 9,
                              borderLeft: "2px solid rgba(0, 0, 0, 0.1)"
                            }}
                          >
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() =>
                                removeItem(item.item_id, item.category)
                              }
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}

              {/* <ItemTable
            items={selectedItemsList}
            setItems={setSelectedItemsList}
          /> */}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Standalone;
